import { Event, Page, Person, SubTopic, Topic, TopicExpertise, Trend } from "@shapeable/gesda-types";
import { isInvitedContribution, isWorkshopEvent, isLandmarkReportPage, isSenseMakingPage } from "../types";

/** Stub page used for global nav highlight (ActiveEntityProvider), not a CMS parent relation. */
export type NavContextPage = Pick<Page, 'id' | 'slug' | 'path' | 'name'>;

export const WORKSHOPS_NAV_PAGE: NavContextPage = {
  id: '65c55d169e947c438698afe7',
  slug: 'workshops',
  path: '/workshops',
  name: 'Workshops',
};

export const COMMUNITY_NAV_PAGE: NavContextPage = {
  id: '6a90ce03ed4227c76a538e35',
  slug: 'community',
  path: '/community',
  name: 'Community',
};

/** Person with optional synthetic parent for global nav (not a CMS page-tree parent). */
export type PersonWithNavParent = Person & { parent?: NavContextPage };

export const transformPage: (entity: Page) => Page = (entity) => {
  let transformedPage = { ...entity };

  if (isInvitedContribution(transformedPage)) {
    // @ts-ignore
    transformedPage.__typename = 'InvitedContribution';
    transformedPage._schema = { label: 'Invited Contribution', pluralLabel: 'Invited Contributions' };
  }

  if (isLandmarkReportPage(transformedPage)) {
    // @ts-ignore
    transformedPage.__typename = 'LandmarkReport';
    transformedPage.typeLabel = 'Landmark Report';
    transformedPage._schema = { label: 'Landmark Report', pluralLabel: 'Landmark Reports' };
  }

  if (isSenseMakingPage(transformedPage)) {
    // @ts-ignore
    transformedPage.__typename = 'SenseMaker';
    transformedPage.typeLabel = 'Sense Maker';
    transformedPage._schema = { label: 'Sense Maker', pluralLabel: 'Sense Makers' };
  }

  // also pull the color from related trend, so that we can show color strips
  transformedPage.color = transformedPage.trend?.color;

  // transform connected pages too

  if (transformedPage.connectedPages) {
    transformedPage.connectedPages = transformedPage.connectedPages.map(transformPage);
  }
  
  return transformedPage;
};

export const transformEvent: (entity: Event) => Event = (entity) => {
  let transformedEvent = { ...entity };

  if (isWorkshopEvent(transformedEvent)) {
    // @ts-ignore
    transformedEvent.parent = WORKSHOPS_NAV_PAGE;
    transformedEvent.__typename = 'Workshop';
    transformedEvent.typeLabel = 'Workshop';
    transformedEvent._schema = { label: 'Workshop', pluralLabel: 'Workshops' };
  }

  return transformedEvent;

};


export const transformPeopleEvents: (entity: Person) => Person = (entity) => ({
  ...entity,
  relatedEvents: [
    ...(entity.relatedEvents || []).map(transformEvent)
  ],
});


export const transformTrend: (entity: Trend) => Trend = (entity) => {
  const transformed = { 
    ...entity,
    pages: [
      ...(entity.pages || []).map(transformPage).map(entity => ({
        ...entity,
        color: entity.color
      }))
    ],
    events: [
      ...(entity.events || []).map(transformEvent)
    ],

  };
  
  return transformed;
};

export const transformTopic: (entity: Trend) => Trend = (entity) => {
  const transformed = { 
    ...entity,
    pages: [
      ...(entity.pages || []).map(transformPage).map(entity => ({
        ...entity,
        color: entity.color
      }))
    ],
  };
  
  if (transformed.connectedPages) {
    transformed.connectedPages = transformed.connectedPages.map(transformPage);
  }
  

  return transformed;
};



export const transformSubTopic: (entity: SubTopic) => SubTopic = (entity) => {
  const transformed = { 
    ...entity,
  };
  
  if (transformed.connectedPages) {
    transformed.connectedPages = transformed.connectedPages.map(transformPage);
  }

  return transformed;
};


export const transformPeoplePage: (entity: Person) => PersonWithNavParent = (entity) => ({
  ...entity,
  parent: COMMUNITY_NAV_PAGE,
});

// Previously only ran transformPeople on committee members (Community nav stub).
// That does not affect topic-page nav; use committeeMembers directly in topic-view-layout.
// export const transformTopicExperts: (entities: Person[]) => Person[] = (entities) => {
//   return entities.map(transformPeople);
// };

// Need to validate if this is still needed
export const transformTopicExpertsByEdition: (entities: Person[]) => Person[] = (entities) => {
  return entities.map((person) => {
    const hasCurrent2025Edition = person.topicExpertise?.some((expertise: TopicExpertise) => expertise.editions?.some(edition => edition.name === '2026'));
    if (hasCurrent2025Edition) {
      return {
        ...person,
        badge: '2026'
      }
    } else {
      return person;
    }
  })
}
